import { t } from '../../../shared/config/translations'
import { consumeAuthNotice } from '../../api/auth'
import { startDiscogsLogin } from '../../auth/discogsAuth'
import { showSnackbar } from '../../components/Snackbar'
import { initRecordPlayerArm } from './recordPlayerArm'

export class LoginPageController {
	private disposeArm: () => void = () => {}
	private readonly rootEl: HTMLElement
	private readonly trackEl: HTMLElement | null
	private readonly slideEls: HTMLElement[]
	private readonly dotEls: HTMLButtonElement[]
	private readonly prevButtonEl: HTMLButtonElement | null
	private readonly nextButtonEl: HTMLButtonElement | null
	private readonly loginButtonEl: HTMLButtonElement | null
	private activeIndex = 0
	private scrollRafId: number | null = null
	private pendingIndex: number | null = null
	private mouseDrag: MouseDrag | null = null

	constructor(rootEl: HTMLElement) {
		this.rootEl = rootEl
		this.trackEl = rootEl.querySelector('[data-tour-track]')
		this.slideEls = [
			...rootEl.querySelectorAll<HTMLElement>('[data-tour-slide]'),
		]
		this.dotEls = [
			...rootEl.querySelectorAll<HTMLButtonElement>('[data-tour-dot]'),
		]
		this.prevButtonEl = rootEl.querySelector('[data-tour-prev]')
		this.nextButtonEl = rootEl.querySelector('[data-tour-next]')
		this.loginButtonEl = rootEl.querySelector('[data-discogs-login]')
		this.handleTrackScroll = this.handleTrackScroll.bind(this)
		this.handleUserScrollStart = this.handleUserScrollStart.bind(this)
		this.handlePageKeydown = this.handlePageKeydown.bind(this)
		this.handleMouseDragStart = this.handleMouseDragStart.bind(this)
		this.handleMouseDragMove = this.handleMouseDragMove.bind(this)
		this.handleMouseDragEnd = this.handleMouseDragEnd.bind(this)
		this.handleDotClick = this.handleDotClick.bind(this)
		this.handleDotKeydown = this.handleDotKeydown.bind(this)
		this.handlePrevClick = this.handlePrevClick.bind(this)
		this.handleNextClick = this.handleNextClick.bind(this)
		this.handleLoginClick = this.handleLoginClick.bind(this)

		this.init()
	}

	private init(): void {
		this.disposeArm = initRecordPlayerArm(this.rootEl)
		this.addEventListeners()
		this.setActiveSlide(0)
		this.showAuthNotice()
	}

	show(): void {}
	hide(): void {}
	dispose(): void {
		this.disposeArm()
		if (this.scrollRafId !== null) cancelAnimationFrame(this.scrollRafId)
		this.trackEl?.removeEventListener('scroll', this.handleTrackScroll)
		for (const type of USER_SCROLL_EVENTS) {
			this.trackEl?.removeEventListener(type, this.handleUserScrollStart)
		}
		document.removeEventListener('keydown', this.handlePageKeydown)
		this.trackEl?.removeEventListener(
			'pointerdown',
			this.handleMouseDragStart,
		)
		this.trackEl?.removeEventListener(
			'pointermove',
			this.handleMouseDragMove,
		)
		this.trackEl?.removeEventListener('pointerup', this.handleMouseDragEnd)
		this.trackEl?.removeEventListener(
			'pointercancel',
			this.handleMouseDragEnd,
		)
		for (const dotEl of this.dotEls) {
			dotEl.removeEventListener('click', this.handleDotClick)
			dotEl.removeEventListener('keydown', this.handleDotKeydown)
		}
		this.prevButtonEl?.removeEventListener('click', this.handlePrevClick)
		this.nextButtonEl?.removeEventListener('click', this.handleNextClick)
		this.loginButtonEl?.removeEventListener('click', this.handleLoginClick)
	}

	private addEventListeners(): void {
		this.trackEl?.addEventListener('scroll', this.handleTrackScroll, {
			passive: true,
		})
		for (const type of USER_SCROLL_EVENTS) {
			this.trackEl?.addEventListener(type, this.handleUserScrollStart, {
				passive: true,
			})
		}
		document.addEventListener('keydown', this.handlePageKeydown)
		this.trackEl?.addEventListener('pointerdown', this.handleMouseDragStart)
		this.trackEl?.addEventListener('pointermove', this.handleMouseDragMove)
		this.trackEl?.addEventListener('pointerup', this.handleMouseDragEnd)
		this.trackEl?.addEventListener('pointercancel', this.handleMouseDragEnd)
		for (const dotEl of this.dotEls) {
			dotEl.addEventListener('click', this.handleDotClick)
			dotEl.addEventListener('keydown', this.handleDotKeydown)
		}
		this.prevButtonEl?.addEventListener('click', this.handlePrevClick)
		this.nextButtonEl?.addEventListener('click', this.handleNextClick)
		this.loginButtonEl?.addEventListener('click', this.handleLoginClick)
	}

	private async handleLoginClick(): Promise<void> {
		if (!this.loginButtonEl) return
		this.loginButtonEl.disabled = true
		try {
			await startDiscogsLogin()
		} catch (error) {
			console.error('Could not start Discogs login:', error)
			showSnackbar(t('auth.loginFailed'), 'error')
		} finally {
			this.loginButtonEl.disabled = false
		}
	}

	// Swipes and snap scrolling drive the active slide; buttons only request a scroll.
	private handleTrackScroll(): void {
		if (this.scrollRafId !== null) return
		this.scrollRafId = requestAnimationFrame(() => {
			this.scrollRafId = null
			if (!this.trackEl || this.trackEl.clientWidth === 0) return
			const index = Math.round(
				this.trackEl.scrollLeft / this.trackEl.clientWidth,
			)
			// A requested scroll passes other slides; keep its target until it arrives.
			if (this.pendingIndex !== null) {
				if (index !== this.pendingIndex) return
				this.pendingIndex = null
			}
			this.setActiveSlide(index)
		})
	}

	/** Lets a swipe or wheel take over from an unfinished requested scroll. */
	private handleUserScrollStart(): void {
		this.pendingIndex = null
	}

	// The tour is the whole page, so arrow keys work without focusing it first.
	private handlePageKeydown(event: KeyboardEvent): void {
		const step = arrowKeyStep(event.key)
		if (
			step === 0 ||
			event.defaultPrevented ||
			event.altKey ||
			event.ctrlKey ||
			event.metaKey ||
			event.shiftKey ||
			isEditableTarget(event.target) ||
			document.querySelector('dialog[open]')
		) {
			return
		}
		event.preventDefault()
		this.scrollToSlide(this.activeIndex + step)
	}

	// Touch and trackpads scroll the track natively; a mouse needs a drag to swipe.
	private handleMouseDragStart(event: PointerEvent): void {
		// The record-player arm claims its own drags by preventing default.
		if (
			!this.trackEl ||
			event.pointerType !== 'mouse' ||
			event.button !== 0 ||
			event.defaultPrevented
		) {
			return
		}
		this.mouseDrag = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startScrollLeft: this.trackEl.scrollLeft,
			startIndex: this.activeIndex,
		}
		this.trackEl.setPointerCapture(event.pointerId)
		this.trackEl.classList.add('is-mouse-dragging')
	}

	private handleMouseDragMove(event: PointerEvent): void {
		if (!this.trackEl || this.mouseDrag?.pointerId !== event.pointerId)
			return
		this.trackEl.scrollLeft =
			this.mouseDrag.startScrollLeft -
			(event.clientX - this.mouseDrag.startX)
	}

	private handleMouseDragEnd(event: PointerEvent): void {
		const drag = this.mouseDrag
		if (!this.trackEl || drag?.pointerId !== event.pointerId) return
		this.mouseDrag = null
		this.trackEl.classList.remove('is-mouse-dragging')
		if (this.trackEl.hasPointerCapture(event.pointerId)) {
			this.trackEl.releasePointerCapture(event.pointerId)
		}

		const deltaX = event.clientX - drag.startX
		const step =
			Math.abs(deltaX) < MOUSE_SWIPE_THRESHOLD_PX
				? 0
				: deltaX < 0
					? 1
					: -1
		this.scrollToSlide(drag.startIndex + step)
	}

	private handleDotClick(event: MouseEvent): void {
		const dotEl = event.currentTarget as HTMLButtonElement
		this.scrollToSlide(Number(dotEl.dataset.tourDot))
	}

	// Tabs use a roving tabindex, so arrow keys move focus between the dots.
	private handleDotKeydown(event: KeyboardEvent): void {
		const step = arrowKeyStep(event.key)
		if (step === 0) return
		event.preventDefault()
		const index = this.scrollToSlide(this.activeIndex + step)
		this.dotEls[index]?.focus()
	}

	private handlePrevClick(): void {
		this.scrollToSlide(this.activeIndex - 1)
	}

	private handleNextClick(): void {
		this.scrollToSlide(this.activeIndex + 1)
	}

	/** Scrolls to a clamped slide index and returns it. */
	private scrollToSlide(index: number): number {
		const target = clamp(index, 0, this.slideEls.length - 1)
		if (!this.trackEl) return target

		const reduceMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)',
		).matches
		const left = target * this.trackEl.clientWidth
		// No scroll event fires when the track is already there to clear it.
		this.pendingIndex =
			Math.abs(this.trackEl.scrollLeft - left) < 1 ? null : target
		this.trackEl.scrollTo({
			left,
			behavior: reduceMotion ? 'auto' : 'smooth',
		})
		this.setActiveSlide(target)
		return target
	}

	private setActiveSlide(index: number): void {
		const target = clamp(index, 0, this.slideEls.length - 1)
		this.activeIndex = target

		this.slideEls.forEach((slideEl, slideIndex) => {
			const isActive = slideIndex === target
			// Scene animations restart each time their slide becomes active.
			slideEl.classList.toggle('is-active', isActive)
			slideEl.setAttribute('aria-hidden', String(!isActive))
		})
		this.dotEls.forEach((dotEl, dotIndex) => {
			const isActive = dotIndex === target
			dotEl.setAttribute('aria-selected', String(isActive))
			dotEl.tabIndex = isActive ? 0 : -1
		})
		if (this.prevButtonEl) this.prevButtonEl.disabled = target === 0
		if (this.nextButtonEl) {
			this.nextButtonEl.disabled = target === this.slideEls.length - 1
		}
	}

	private showAuthNotice(): void {
		const authNotice = consumeAuthNotice()
		if (authNotice) showSnackbar(authNotice, 'error')
	}
}

type MouseDrag = {
	pointerId: number
	startX: number
	startScrollLeft: number
	startIndex: number
}

const USER_SCROLL_EVENTS = ['pointerdown', 'touchstart', 'wheel'] as const
const MOUSE_SWIPE_THRESHOLD_PX = 40

function isEditableTarget(target: EventTarget | null): boolean {
	return (
		target instanceof HTMLElement &&
		(target.isContentEditable ||
			['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName))
	)
}

function arrowKeyStep(key: string): number {
	if (key === 'ArrowRight') return 1
	if (key === 'ArrowLeft') return -1
	return 0
}

function clamp(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, value))
}
